import { Navigate, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { helpdeskSearchSchema } from '@features/helpdesk';
import {
  AiContentList,
  AiContentDetail,
  AiContentCreate,
  aiContentSearchSchema,
  type AiContentSearch,
} from '@features/helpdesk-ai-content';
import type { HelpdeskAiContentKind } from '@shared/api';
import {
  canAccessHelpdesk,
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
} from '@shared/lib';
import { PageBackLink } from '@shared/ui';
import { CrmKnowledgeNavigation } from './CrmKnowledgeNavigation';
import { CrmPrimaryNavigation } from './CrmPrimaryNavigation';

export const aiContentRouteSearchSchema = helpdeskSearchSchema
  .extend(aiContentSearchSchema.shape)
  .extend({ edit: z.literal('1').optional() });
type Search = ReturnType<typeof aiContentRouteSearchSchema.parse>;

export function CrmHelpdeskAiContentPage({
  locale,
  kind,
  search,
  id,
  create = false,
}: {
  locale: string;
  kind: HelpdeskAiContentKind;
  search: Search;
  id?: string;
  create?: boolean;
}) {
  const { t } = useTranslation();
  const ability = useAbility();
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const scope = user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : '';
  const subject = kind === 'prompts' ? 'HelpdeskAiPrompt' : 'HelpdeskAiInstruction';
  if (!ability.can('read', subject))
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  const listPath =
    kind === 'prompts' ? '/$locale/crm/helpdesk/prompts' : '/$locale/crm/helpdesk/instructions';
  const back = () => {
    void navigate({ to: listPath, params: { locale }, search: { ...search, edit: undefined } });
  };
  const open = (contentId: string, edit?: '1', replace = false) => {
    if (kind === 'prompts') {
      void navigate({
        to: '/$locale/crm/helpdesk/prompts/$promptId',
        params: { locale, promptId: contentId },
        search: { ...search, edit },
        replace,
        resetScroll: !replace,
      });
    } else {
      void navigate({
        to: '/$locale/crm/helpdesk/instructions/$instructionId',
        params: { locale, instructionId: contentId },
        search: { ...search, edit },
        replace,
        resetScroll: !replace,
      });
    }
  };
  const changeSearch = (next: AiContentSearch) => {
    void navigate({
      to: listPath,
      params: { locale },
      search: { ...search, ...next },
      replace: true,
      resetScroll: false,
    });
  };
  return (
    <main className="flex min-w-0 flex-col gap-4 p-4 pb-24 sm:p-6 sm:pb-24">
      {create ? (
        <AiContentCreate
          key={`${user?.id ?? ''}:${kind}:new`}
          kind={kind}
          scope={scope}
          canCreate={ability.can('create', subject)}
          onBack={back}
          onCreated={(contentId) => {
            open(contentId);
          }}
        />
      ) : !id ? (
        <>
          <CrmPrimaryNavigation
            className="mb-0"
            activeSection="helpdesk"
            locale={locale}
            canAccessHelpdesk={canAccessHelpdesk(ability)}
            canAccessProfiles={canAccessCrmCustomerDirectory(ability)}
            canAccessMemberships={canAccessCrmCustomerMembershipDirectory(ability)}
          />
          {canAccessHelpdesk(ability) ? (
            <PageBackLink
              onClick={() => {
                void navigate({
                  to: '/$locale/crm/helpdesk',
                  params: { locale },
                  search: helpdeskSearchSchema.parse(search),
                });
              }}
            >
              {t('articles.backRequests')}
            </PageBackLink>
          ) : null}
          <CrmKnowledgeNavigation locale={locale} active={kind} search={search} />
          <AiContentList
            key={`${user?.id ?? ''}:${kind}`}
            kind={kind}
            scope={scope}
            search={search}
            onSearchChange={changeSearch}
            onOpen={open}
            canDelete={ability.can('delete', subject)}
            onCreate={
              ability.can('create', subject)
                ? () => {
                    void navigate({
                      to:
                        kind === 'prompts'
                          ? '/$locale/crm/helpdesk/prompts/new'
                          : '/$locale/crm/helpdesk/instructions/new',
                      params: { locale },
                      search: { ...search, edit: undefined },
                    });
                  }
                : undefined
            }
            onEdit={
              ability.can('update', subject)
                ? (contentId) => {
                    open(contentId, '1');
                  }
                : undefined
            }
          />
        </>
      ) : (
        <AiContentDetail
          key={`${user?.id ?? ''}:${kind}:${id}`}
          kind={kind}
          scope={scope}
          id={id}
          canUpdate={ability.can('update', subject)}
          canDelete={ability.can('delete', subject)}
          onBack={back}
          startInEditMode={search.edit === '1'}
          onEditEntryConsumed={() => {
            open(id, undefined, true);
          }}
        />
      )}
    </main>
  );
}
